import type { CountryCatalogItem } from '@voll-intelligence/intelligence-ui-kit';
import type {
  CheckoutBudget,
  CheckoutBudgetQuery,
  CheckoutTripService,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useGenUiConfig } from '../../shared/ui/config.js';
import type {
  ChatUiOption,
  CorporateBookingForm,
  ConciergeReasonOption,
  ConciergeReasonProductType,
  ConciergeRentalOptions,
  CheckoutProductType,
} from '@voll-intelligence/genui-contract';
import type { CheckoutOutcome } from './core/use_one_click_checkout.js';

export type { CheckoutProductType } from '@voll-intelligence/genui-contract';

/**
 * Single routed port (query-only IO) for all unified-checkout drawers —
 * one shape keyed by `productType`. Injected by the host via
 * `GenUiConfig.ports.productCheckout`. Ausente ⇒ drawer degrada fail-soft.
 */
export interface ProductCheckoutPort {
  getBookingForm: (
    productType: CheckoutProductType,
    travelerId?: string,
  ) => Promise<CorporateBookingForm>;
  getBudget: (productType: CheckoutProductType, q: CheckoutBudgetQuery) => Promise<CheckoutBudget>;
  /**
   * Os serviços JÁ comprados da viagem deste checkout — o que o painel "Sua
   * viagem" mostra ao lado (e separado) do que está sendo solicitado agora
   * (#3180). OPCIONAL: host sem ela ⇒ o painel renderiza só o carrinho, como
   * antes.
   *
   * Recebe a viagem do BLOCO, e só ela — nunca uma viagem que o host adivinhe
   * de outra tela (a vinculada à conversa ativa, a da rota): o cartão afirma
   * "já na viagem" sobre a compra em curso, e uma viagem de outro contexto
   * afirmaria isso sobre a errada. Bloco sem `tripId` (replay, produtor que
   * ainda não emite a chave) não consulta nada. Sem viagem legível ⇒ `[]`,
   * nunca um erro: o checkout não depende disto para acontecer.
   */
  getTripServices?: (tripId: string) => Promise<CheckoutTripService[]>;
  /**
   * Busca centros de custo no servidor, escopada pela empresa. OPCIONAL: sem ela
   * o campo segue com o catálogo embutido no form (host legado, preview,
   * Storybook, testes).
   *
   * O catálogo de `form.companies[].costCenters` não é exaustivo — a empresa
   * marcada `allCostCenters` (ou que devolve a lista vazia) delega a resolução
   * para cá, igual ao app real. Sem esta busca o cliente grande fica com um
   * `<select>` mudo e a reserva travada (#2459).
   */
  searchCostCenters?: (companyId: string, term?: string) => Promise<ChatUiOption[]>;
  /**
   * O catálogo "por que pedir à equipe" do pedido OFFLINE (`concierge_checkout`),
   * por produto. OPCIONAL: só o host da bancada determinística o injeta; sem
   * ele o checkout de concierge segue sem o campo de motivo do pedido (o
   * motivo corporativo continua obrigatório).
   */
  getConciergeReasons?: (
    productType: ConciergeReasonProductType,
  ) => Promise<ConciergeReasonOption[]>;
  /**
   * Os catálogos do pedido OFFLINE de carro (categorias e locadoras do
   * rental-car-api). OPCIONAL, mas sem ele o pedido de carro não fecha: a
   * categoria é obrigatória no fornecedor e não há de onde escolhê-la.
   */
  getRentalConciergeOptions?: () => Promise<ConciergeRentalOptions>;
  /**
   * O catálogo de países da nacionalidade e do país emissor — o MESMO do perfil
   * (`/profile/countries`, traduzido pelo servidor em `label`). OPCIONAL e
   * fail-closed: sem ela, ou com a lista vazia/indisponível, os dois campos
   * ficam só-leitura com o valor gravado (#3669), nunca um texto livre.
   */
  getCountries?: () => Promise<CountryCatalogItem[]>;
}

/**
 * O fecho do 1click (#5279), dito pelo host: só ele vê o turno da reserva
 * chegar (o `*_next_services` do agente) e
 * só ele sabe onde fica a viagem na tela. Ausente ⇒ o card nunca diz
 * «Solicitado com sucesso!» por conta própria (o congelamento o encerra) e não há
 * voo até a viagem.
 */
export interface OneClickPort {
  /** Hook: o desfecho do pedido deste challenge, reativo. Nome `use*` porque
   *  é chamado no corpo do card, sob as regras de hooks. Devolve a MESMA
   *  referência enquanto o desfecho não muda (é um objeto desde o `failed`,
   *  #5514). */
  useOutcome: (challengeId: string) => CheckoutOutcome | undefined;
  /**
   * A chegada à viagem: a bolinha parte de `from` (o botão que acabou de
   * encolher) e o host a leva até a mala ou ao topo do painel da viagem. A
   * promessa assenta quando o serviço entrou na viagem; o card só então vira
   * «Solicitado com sucesso!». Nunca rejeita de propósito, e o card trata uma
   * rejeição como chegada.
   */
  arrive: (from: DOMRect) => Promise<void>;
  /**
   * A viagem já está à vista (o painel aberto ao lado da conversa, #6329): a
   * chegada é no lugar — o botão não encolhe até a bolinha, o host aplica o
   * pedido e realça a parada nova no painel, e o card vira o registro ✓ ali
   * mesmo. Lido uma vez, quando o pedido entra. Ausente = sempre a bolinha.
   */
  arrivesInline?: () => boolean;
}

/** Um programa gravado no perfil de quem está logado: o `id` do catálogo
 *  (`LOYALTY_PROGRAMS`) e o número. O host resolve o código gravado no programa
 *  (`programByCode`): uma linha gravada com «JJ» chega como `latam-pass`. */
export interface LoyaltyPlan {
  programId: string;
  number: string;
}

/** A leitura dos programas, nos três estados do GET do perfil. */
export type LoyaltyPlans =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; plans: readonly LoyaltyPlan[] };

/**
 * A fidelidade do perfil no card 1click (#5340 aéreo, #6159 carro e hotel): o
 * card mostra, por companhia dos trechos (ou pela locadora, ou pela rede do
 * hotel), o programa que o perfil tem ou «Adicionar programa de fidelidade». Só
 * display — nenhuma reserva leva o número; basta estar no perfil. Ausente ⇒ o
 * card não tem a linha; sem a leitura de um tipo, o card daquele tipo também não.
 *
 * O perfil é sempre o de quem está LOGADO: o card só lê com o viajante igual a
 * ele, nunca para um colega.
 */
export interface LoyaltyPort {
  /** Hook: os programas aéreos do perfil, reativos (a linha vira «identificada»
   *  quando a pessoa grava pela tela do perfil). Nome `use*` porque é chamado no
   *  corpo do card, sob as regras de hooks. */
  useAirPlans: () => LoyaltyPlans;
  /** Os programas de hotel do perfil, como `useAirPlans`. */
  useHotelPlans?: () => LoyaltyPlans;
  /** Os programas de locadora do perfil, como `useAirPlans`. */
  useRentalPlans?: () => LoyaltyPlans;
  /** A logo de um programa de hotel ou de locadora do catálogo (o aéreo usa a
   *  da companhia); `null` sem logo, e a linha mostra o ícone do tipo. */
  logoOf?: (programId: string) => string | null;
  /** Abre a tela do perfil (Preferências › Fidelidade) já no formulário deste
   *  programa do catálogo. */
  openAdd: (programId: string) => void;
}

/** Lê o port do Context. `null` quando ausente (preview/console/testes). */
export function useProductCheckoutPort(): ProductCheckoutPort | null {
  return useGenUiConfig().ports?.productCheckout ?? null;
}
